/**
 * Frontend view of the backend's `TestMappingForSkuResult` union
 * (apps/backend/src/modules/shopify/metafield-mappings/metafield-mappings.handler.ts).
 *
 * DERIVED from the router output via `RouterOutputs` (post-impl review fix)
 * — no hand-mirrored copy to drift. Both consumers (SkuTestSection's per-row
 * test and SkuPreviewPanel's card-level preview) import from here.
 *
 * The `present` arm carries the Plan 06 dual-aware contract verbatim:
 * `{ frontFilename, backFilename, isDual, unrecognizedValues }`. The
 * presentation inversion is already mapped away server-side —
 * `frontFilename` IS the front of the garment. The legacy single-shape
 * `derivedFilename` field was retired by Plan 08 (no reader remained).
 */

import type { RouterOutputs } from '@/lib/trpc';

/**
 * Same SKU character class as the backend router's Zod schema. Rejects
 * Shopify search-syntax operators client-side; the server validates again
 * as defense in depth. Shared by both per-SKU test affordances.
 */
export const SKU_INPUT_REGEX = /^[A-Za-z0-9._-]{1,64}$/;

export type SkuTestResult =
  RouterOutputs['shopifyMetafieldMappings']['testMappingForSku'];

/**
 * One misconfigured metafield value flagged by the preview. `rawValue: ''`
 * means the value is MISSING where the SKU encoding requires it (e.g. a
 * dual-encoded SKU with a side's design ID unset). Narrowed off the derived
 * union so it can never drift from the backend shape.
 */
export type UnrecognizedMetafieldValue = Extract<
  SkuTestResult,
  { status: 'present' }
>['unrecognizedValues'][number];
